<template>
    <div class="register__content">
        <h1>{{ title }}</h1>
        <hr />
        <div class="register__content__email">
            <template v-if="hasEmailSent">
                <div class="email-sent-complete">
                    <p class="description email-sent">
                        <strong>{{ sentEmail }}</strong
                        >으로
                    </p>
                    <p class="description email-sent">
                        인증 메일 전송이 완료 되었습니다.
                    </p>
                </div>
                <div class="email-sent-complete">
                    <p class="description email-sent">
                        메일을 확인 후 메일에 있는
                        <strong>“메일 인증하기“</strong> 버튼을 클릭해 주세요.
                    </p>
                    <p class="description email-sent">
                        혹시 메일을 받지 못하셨다면 아래 재 전송 버튼을 클릭해
                        주세요.
                    </p>
                </div>

                <NButton
                    class="send-again"
                    attr-type="submit"
                    type="primary"
                    strong
                    size="large"
                    @click="sendEmailAgain"
                    >인증메일 재전송</NButton
                >
            </template>

            <template v-else>
                <p class="description">
                    소속 이메일을
                    {{ isSkipEmailVerification ? '입력' : '인증' }}해 주세요.
                </p>
                <Form
                    class="form"
                    :validation-schema="schema"
                    @submit="handleSubmit"
                    v-slot="{meta}"
                >
                    <vee-input
                        class="form__input"
                        :label="'이메일'"
                        :type="'email'"
                        :name="'email'"
                        :placeholder="'회사 이메일을 입력해주세요.'"
                    />

                    <NButton
                        attr-type="submit"
                        class="form__submit"
                        type="primary"
                        strong
                        size="large"
                        :disabled="!meta.valid"
                        >{{
                            isSkipEmailVerification ? '다음' : '인증메일 전송'
                        }}</NButton
                    >
                </Form>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    sendEmailVerification as sendEmailVerificationApi,
    validateEmail as validateEmailApi,
} from '@/api/user';
import VeeInput from '@/components/VeeInput.vue';
import {decodeFromBase64, encodeIntoBase64} from '@/utils/buffer';
import * as Sentry from '@sentry/vue';
import {AxiosError} from 'axios';
import {NButton} from 'naive-ui';
import {Form} from 'vee-validate';
import {ref, toRefs} from 'vue';
import {useRouter} from 'vue-router';
import * as Yup from 'yup';

// props, context, emits, libs
const props = defineProps<{
    isSkipEmailVerification: boolean; // 이메일 인증 생략 여부 (복지몰회원(companyId: 487)사만 이메일 인증 생략함)
    companyIds: {
        encodedCompanyId: string | undefined;
        decodedCompanyId: number | null;
    };
    encodedCompanyName: string | undefined;
}>();

const schema = Yup.object().shape({
    email: Yup.string()
        .email('올바른 이메일 주소를 입력해주세요.')
        .required('이메일을 입력해주세요.'),
});

const router = useRouter();

// ref, reactive
const {encodedCompanyId, decodedCompanyId} = toRefs(props.companyIds);
const {isSkipEmailVerification, encodedCompanyName} = toRefs(props);

const sentEmail = ref('');
const hasEmailSent = ref(false);

const title = ref('이메일 확인');

// computed
// methods
const validateEnteredEmail = async (email: string) => {
    try {
        await validateEmailApi(email, decodedCompanyId.value as number);

        router.push({
            path: '/register/afterInvitation',
            query: {
                e: encodeIntoBase64(email),
                ci: encodedCompanyId.value,
                cn: encodedCompanyName.value,
            },
        });
    } catch (err) {
        const axiosError = err as AxiosError;

        Sentry.captureException(axiosError, {
            level: 'error',
            extra: {
                code: axiosError.code,
                status: axiosError.response?.status,
                request: axiosError.request,
                response: axiosError.response?.data,
                message: axiosError.message,
                from: 'src/components/register/EmailForm.vue > validateInputEmail',
            },
        });

        console.error(axiosError.message);
        window.customAlert(axiosError.message);
    }
};

const verifyEmail = async (email: string) => {
    try {
        await sendEmailVerificationApi({
            receiverEmail: email,
            ...(encodedCompanyId.value && {
                companyId: Number(decodeFromBase64(encodedCompanyId.value)),
            }),
            ...(encodedCompanyName.value && {
                companyName: decodeFromBase64(encodedCompanyName.value),
            }),
        });

        hasEmailSent.value = true;
        sentEmail.value = email;
        title.value = '인증 메일이 발송 되었습니다.';
    } catch (err) {
        const axiosError = err as AxiosError;

        Sentry.captureException(axiosError, {
            level: 'error',
            extra: {
                code: axiosError.code,
                status: axiosError.response?.status,
                request: axiosError.request,
                response: axiosError.response?.data,
                message: axiosError.message,
                from: 'src/components/register/EmailForm.vue',
            },
        });

        console.error(axiosError.message);
        window.customAlert(axiosError.message);
    }
};

const handleSubmit = async (inputValues: any) => {
    if (isSkipEmailVerification.value && decodedCompanyId.value) {
        // NOTE: 복지몰회원(companyId: 487)사인 경우, 이메일 인증을 생략하고 이메일 검증만 거침.
        // 검증 성공 시, 바로 약관동의 페이지로 push
        return validateEnteredEmail(inputValues.email);
    }

    // NOTE: 복지몰회원(companyId: 487)사가 아닌 경우, 이메일 인증 진행
    verifyEmail(inputValues.email);
};

const sendEmailAgain = async () => {
    try {
        await sendEmailVerificationApi({
            receiverEmail: sentEmail.value,
            ...(encodedCompanyId.value && {
                companyId: Number(decodeFromBase64(encodedCompanyId.value)),
            }),
            ...(encodedCompanyName.value && {
                companyName: decodeFromBase64(encodedCompanyName.value),
            }),
        });
        window.customAlert('인증 메일이 재 발송 되었습니다.');
    } catch (err) {
        const axiosError = err as AxiosError;

        Sentry.captureException(axiosError, {
            level: 'error',
            extra: {
                code: axiosError.code,
                status: axiosError.response?.status,
                header: axiosError.config.headers,
                request: axiosError.request,
                response: axiosError.response?.data,
                message: axiosError.message,
                from: 'src/components/register/EmailForm.vue',
            },
        });

        console.error(axiosError.message);
        window.customAlert(axiosError.message);
    }
};

// effect (created, mounted, watch, VueUse)
</script>

<style lang="scss" scoped>
.register {
    &__content {
        width: 376px;

        @media (max-width: 500px) {
            min-width: 280px;
            max-width: 376px;
            padding: 0 16px;
        }

        h1 {
            font-size: 18px;
            font-weight: 700;
            margin: 16px 0;
        }

        hr {
            color: $black_04;
        }

        &__email {
            margin-top: 16px;

            .description {
                font-size: 14px;
            }

            .email-sent-complete {
                margin-bottom: 32px;
                word-break: keep-all;
            }

            .email-sent {
                color: $black_48;

                strong {
                    color: #000;
                }
            }

            .caption {
                display: inline-block;
                color: #5c5c5c;
                font-size: 12px;
                margin: 8px 0;
            }

            .send-again {
                width: 100%;
            }
        }

        .form {
            margin-top: 16px;

            button {
                width: 100%;
                margin-top: 8px;
                font-weight: 700;
            }
        }
    }
}
</style>
